import { useNavigate } from "react-router-dom";
import { Users, Building, Sparkles, Calendar, Film, Camera, Star } from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import PublicNavbar from "../components/PublicNavbar";

// NEW: Luminous Hollywood premiere background
const BACKGROUND_IMAGE = "https://customer-assets.emergentagent.com/job_e54d5963-4f37-44ed-8ca3-11e6a6219384/artifacts/4zfnbqje_IMG_3607.png";

// Generate random particles
const particles = [...Array(25)].map((_, i) => ({
  id: i,
  delay: `${i * 0.4}s`,
  duration: `${10 + Math.random() * 10}s`,
  xStart: `${Math.random() * 100}%`,
  size: `${3 + Math.random() * 5}px`,
  opacity: 0.4 + Math.random() * 0.5,
}));

const PortalSelector = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen relative overflow-hidden" style={{ background: '#0a0500' }}>
      
      {/* ===== LUMINOUS CINEMATIC BACKGROUND ===== */}
      <div 
        className="fixed inset-0 bg-cover bg-center bg-no-repeat"
        style={{ 
          backgroundImage: `url(${BACKGROUND_IMAGE})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center 30%',
        }}
        aria-hidden="true"
      />
      
      {/* Subtle vignette for depth - lighter for luminous feel */}
      <div 
        className="fixed inset-0"
        style={{
          background: 'radial-gradient(ellipse 80% 70% at 50% 40%, transparent 0%, rgba(0,0,0,0.3) 80%, rgba(0,0,0,0.6) 100%)',
        }}
        aria-hidden="true"
      />

      {/* Gold glow overlay for luminosity */}
      <div 
        className="fixed inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse 60% 40% at 50% 30%, rgba(255, 200, 100, 0.08) 0%, transparent 60%)',
        }}
        aria-hidden="true"
      />

      {/* Film Grain Overlay */}
      <div 
        className="fixed inset-0 pointer-events-none"
        style={{
          zIndex: 2,
          opacity: 0.035,
          mixBlendMode: 'overlay',
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%' height='100%' filter='url(%23noise)'/%3E%3C/svg%3E")`,
          animation: 'grain 0.5s steps(10) infinite',
        }}
        aria-hidden="true"
      />

      {/* Animated Gold Particles */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden" style={{ zIndex: 3 }}>
        {particles.map((p) => (
          <div
            key={p.id}
            className="absolute rounded-full"
            style={{
              width: p.size,
              height: p.size,
              background: `radial-gradient(circle, rgba(255, 215, 0, ${p.opacity}) 0%, transparent 70%)`,
              left: p.xStart,
              bottom: '-10px',
              boxShadow: '0 0 8px rgba(255, 215, 0, 0.5)',
              animation: `floatUp ${p.duration} ease-in-out infinite`,
              animationDelay: p.delay,
            }}
          />
        ))}
      </div>

      <PublicNavbar />

      {/* Hero Section - Positioned below background branding */}
      <section className="relative z-10 min-h-[50vh] flex items-end justify-center px-6 pt-24 pb-8" data-testid="landing-hero">
        {/* Hero content intentionally minimal - let background image speak */}
      </section>

      {/* Portal Selection Section */}
      <section className="relative z-10 py-8 px-6" data-testid="portal-section">
        <div className="max-w-6xl mx-auto">
          <h2 
            className="text-center text-xl md:text-2xl font-semibold mb-2" 
            style={{ 
              color: '#FFD700',
              fontFamily: 'Montserrat, sans-serif', 
              textShadow: '0 0 20px rgba(255, 215, 0, 0.4)',
              letterSpacing: '0.1em',
            }}
          >
            ENTER YOUR PORTAL
          </h2>
          <p className="text-center text-[#C9C9C9] mb-8 text-sm" style={{ textShadow: '0 2px 10px rgba(0,0,0,0.8)' }}>
            Select your destination to continue
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Staff Portal */}
            <Card 
              className="group cursor-pointer transition-all duration-500 hover:scale-105"
              style={{
                background: 'linear-gradient(145deg, rgba(20, 15, 5, 0.9) 0%, rgba(30, 20, 5, 0.85) 100%)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255, 215, 0, 0.2)',
                boxShadow: '0 0 30px rgba(255, 200, 100, 0.1), inset 0 1px 0 rgba(255, 215, 0, 0.1)',
              }}
              onClick={() => navigate("/staff/login")}
              data-testid="staff-portal-btn"
            >
              <CardContent className="pt-8 pb-8 text-center relative overflow-hidden">
                {/* Hover glow effect */}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" style={{
                  background: 'radial-gradient(circle at 50% 50%, rgba(255, 215, 0, 0.15) 0%, transparent 70%)',
                }} />
                <div className="relative z-10">
                  <div 
                    className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 transition-all duration-300 group-hover:scale-110"
                    style={{ 
                      background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.3) 0%, rgba(212, 175, 55, 0.2) 100%)',
                      boxShadow: '0 0 20px rgba(255, 215, 0, 0.3)',
                    }}
                  >
                    <Building size={32} className="text-[#FFD700]" />
                  </div>
                  <h2 className="text-xl font-semibold text-[#FFFFFF] mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>Staff Portal</h2>
                  <p className="text-sm text-[#E5C558] mb-3">Team Members</p>
                  <p className="text-xs text-[#A1A1AA]">Internal tools & operations</p>
                </div>
              </CardContent>
            </Card>

            {/* Customer Portal */}
            <Card 
              className="group cursor-pointer transition-all duration-500 hover:scale-105"
              style={{
                background: 'linear-gradient(145deg, rgba(20, 15, 5, 0.9) 0%, rgba(30, 20, 5, 0.85) 100%)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255, 215, 0, 0.2)',
                boxShadow: '0 0 30px rgba(255, 200, 100, 0.1), inset 0 1px 0 rgba(255, 215, 0, 0.1)',
              }}
              onClick={() => navigate("/client/login")}
              data-testid="customer-portal-btn"
            >
              <CardContent className="pt-8 pb-8 text-center relative overflow-hidden">
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" style={{
                  background: 'radial-gradient(circle at 50% 50%, rgba(255, 215, 0, 0.15) 0%, transparent 70%)',
                }} />
                <div className="relative z-10">
                  <div 
                    className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 transition-all duration-300 group-hover:scale-110"
                    style={{ 
                      background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.3) 0%, rgba(212, 175, 55, 0.2) 100%)',
                      boxShadow: '0 0 20px rgba(255, 215, 0, 0.3)',
                    }}
                  >
                    <Users size={32} className="text-[#FFD700]" />
                  </div>
                  <h2 className="text-xl font-semibold text-[#FFFFFF] mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>Client Portal</h2>
                  <p className="text-sm text-[#E5C558] mb-3">Customers</p>
                  <p className="text-xs text-[#A1A1AA]">Invoices, projects & files</p>
                </div>
              </CardContent>
            </Card>

            {/* Booking Portal */}
            <Card 
              className="group cursor-pointer transition-all duration-500 hover:scale-105"
              style={{
                background: 'linear-gradient(145deg, rgba(20, 15, 5, 0.9) 0%, rgba(30, 20, 5, 0.85) 100%)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255, 215, 0, 0.2)',
                boxShadow: '0 0 30px rgba(255, 200, 100, 0.1), inset 0 1px 0 rgba(255, 215, 0, 0.1)',
              }}
              onClick={() => navigate("/booking")}
              data-testid="booking-portal-btn"
            >
              <CardContent className="pt-8 pb-8 text-center relative overflow-hidden">
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" style={{
                  background: 'radial-gradient(circle at 50% 50%, rgba(255, 215, 0, 0.15) 0%, transparent 70%)',
                }} />
                <div className="relative z-10">
                  <div 
                    className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 transition-all duration-300 group-hover:scale-110"
                    style={{ 
                      background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.3) 0%, rgba(212, 175, 55, 0.2) 100%)',
                      boxShadow: '0 0 20px rgba(255, 215, 0, 0.3)',
                    }}
                  >
                    <Camera size={32} className="text-[#FFD700]" />
                  </div>
                  <h2 className="text-xl font-semibold text-[#FFFFFF] mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>Book a Session</h2>
                  <p className="text-sm text-[#E5C558] mb-3">Appointments</p>
                  <p className="text-xs text-[#A1A1AA]">Schedule & pay deposits</p>
                </div>
              </CardContent>
            </Card>

            {/* Promo Boost Portal */}
            <Card 
              className="group cursor-pointer transition-all duration-500 hover:scale-105"
              style={{
                background: 'linear-gradient(145deg, rgba(20, 15, 5, 0.9) 0%, rgba(30, 20, 5, 0.85) 100%)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255, 215, 0, 0.2)',
                boxShadow: '0 0 30px rgba(255, 200, 100, 0.1), inset 0 1px 0 rgba(255, 215, 0, 0.1)',
              }}
              onClick={() => navigate("/promo")}
              data-testid="promo-portal-btn"
            >
              <CardContent className="pt-8 pb-8 text-center relative overflow-hidden">
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" style={{
                  background: 'radial-gradient(circle at 50% 50%, rgba(255, 215, 0, 0.15) 0%, transparent 70%)',
                }} />
                <div className="relative z-10">
                  <div 
                    className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 transition-all duration-300 group-hover:scale-110"
                    style={{ 
                      background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.3) 0%, rgba(212, 175, 55, 0.2) 100%)',
                      boxShadow: '0 0 20px rgba(255, 215, 0, 0.3)',
                    }}
                  >
                    <Star size={32} className="text-[#FFD700]" />
                  </div>
                  <h2 className="text-xl font-semibold text-[#FFFFFF] mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>Promo Boost</h2>
                  <p className="text-sm text-[#E5C558] mb-3">Artists</p>
                  <p className="text-xs text-[#A1A1AA]">Music promotion packages</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 text-center py-8 border-t border-[#D4AF37]/20" style={{ background: 'rgba(10, 5, 0, 0.7)', backdropFilter: 'blur(10px)' }}>
        <p className="text-[#E5C558] text-xs tracking-wider">
          © 2025 DopeInHD Incorporated. All rights reserved.
        </p>
        <p className="text-[#A1A1AA] text-xs mt-1">
          Global Media • Film • Production • Modeling
        </p>
      </footer>
    </div>
  );
};

export default PortalSelector;
